import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { fetchClaimsByFactory } from "../api/claims";
import { useFactory } from "@/core/hooks/useFactory";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Plus, Search, FileText, AlertCircle, LayoutDashboard } from "lucide-react";
import { format } from "date-fns";
import { CreateClaimDialog } from "@/modules/8d/components/CreateClaimDialog";
import { ClaimDetails } from "@/modules/8d/components/ClaimDetails";
import { EightDDashboard } from "@/modules/8d/components/Dashboard";

const statusColors: Record<string, string> = {
  open: "bg-warning/20 text-warning border-warning/30",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  pending_supplier: "bg-chart-3/20 text-chart-3 border-chart-3/30",
  pending_approval: "bg-chart-4/20 text-chart-4 border-chart-4/30",
  closed: "bg-success/20 text-success border-success/30",
};

export default function EightDManagement() {
  const { selectedFactory } = useFactory();
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "dashboard";
  const selectedClaimId = searchParams.get("claim");

  const updateParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });
    setSearchParams(nextParams);
  };

  const { data: claims, isLoading } = useQuery({
    queryKey: ["mod_8d_claims", selectedFactory?.id],
    queryFn: () => fetchClaimsByFactory(selectedFactory!.id),
    enabled: !!selectedFactory?.id,
  });

  const filteredClaims = claims?.filter(
    (claim) =>
      claim.claim_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      claim.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      claim.supplier?.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (selectedClaimId) {
    return (
      <ClaimDetails
        claimId={selectedClaimId}
        onBack={() => updateParams({ claim: null })}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">8D Management</h1>
          <p className="text-muted-foreground">
            Manage supplier claims and 8D reports
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Claim
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => updateParams({ tab: value })}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="claims" className="gap-2">
            <FileText className="h-4 w-4" />
            Claims
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <EightDDashboard />
        </TabsContent>

        <TabsContent value="claims" className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search claims..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading claims...
            </div>
          ) : !filteredClaims?.length ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No claims found</h3>
                <p className="text-muted-foreground text-sm">
                  Create your first 8D claim to get started
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredClaims.map((claim) => (
                <Card
                  key={claim.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => updateParams({ claim: claim.id, tab: "claims" })}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-muted-foreground">
                            {claim.claim_number}
                          </span>
                          <Badge
                            variant="outline"
                            className={statusColors[claim.status] || ""}
                          >
                            {claim.status.replace("_", " ")}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg">{claim.title}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                      <span>
                        <FileText className="h-4 w-4 inline mr-1" />
                        {claim.supplier?.name ? (
                          <Link
                            to={`/supplier-quality/${claim.supplier_id}`}
                            onClick={(event) => event.stopPropagation()}
                            className="text-primary hover:underline"
                          >
                            {claim.supplier.name}
                          </Link>
                        ) : (
                          "Unknown supplier"
                        )}
                      </span>
                      <span>
                        Created {format(new Date(claim.created_at), "MMM d, yyyy")}
                      </span>
                      {claim.creator?.full_name && (
                        <span>by {claim.creator.full_name}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

      </Tabs>

      <CreateClaimDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
